import React, { useReducer, useEffect } from 'react'
import { Flex, IconButton, List, Item, DepartmentSelect, Toaster } from '@ui'
import { Tag, Classes } from '@blueprintjs/core'
import api, { IDepartment, IEmployeeDetails, IManagedDepartment, Role } from '@api'
import { sortBy, uniqBy, ValueIteratee } from 'lodash'
import { ApolloError } from 'apollo-boost'
import { showDepartmentsList } from './EmployeeDetailsDialog'

enum Actions {
  LOAD,
  UPDATE,
}

interface IState {
  reqId: number
  loading: boolean
  managedDepartments: IManagedDepartment[]
}

interface Action {
  type: Actions.LOAD | Actions.UPDATE
  reqId: number
  managedDepartments: IManagedDepartment[]
}

export default function ManagedDepartmentsList({
  readOnly,
  employeeId,
  role,
  empIds = [],
  updateDeptArray,
  bookingManagerSetting,
  employee,
}: {
  readOnly: boolean
  employeeId: string
  role: string
  empIds: any
  updateDeptArray: (params: string[]) => void
  bookingManagerSetting: any
  employee: any
}) {
  const {
    loading,
    managedDepartments,
    addDepartment,
    deleteDepartment,
    addBookingManagerDepartment,
    deleteBookingManagerDepartment,
    reset,
  } = useManagedDepartments(employeeId, role, updateDeptArray, bookingManagerSetting)

  if (loading) {
    return (
      <List style={{ marginTop: 10 }}>
        <Item>
          <span className={Classes.SKELETON}>Placeholder text</span>
        </Item>
        <Item>
          <span className={Classes.SKELETON}>Placeholder text</span>
        </Item>
        <Item>
          <span className={Classes.SKELETON}>Placeholder text</span>
        </Item>
      </List>
    )
  }

  // Do not show mangeged departement section when list is empty
  if (readOnly && !managedDepartments?.length) {
    return null
  }

  return (
    <>
      <div>
        {showDepartmentsList(role, bookingManagerSetting) && (
          <List title="Managed departments" style={{ marginTop: 10 }}>
            {(sortBy(managedDepartments, 'department.name') as IManagedDepartment[]).map(
              ({ department }) => (
                <Item key={department.id}>
                  <Flex align="center" justify="space-between">
                    <div style={{ fontSize: 15 }}>
                      {department.name}{' '}
                      {department.parentDepartment && (
                        <>
                          ({department.parentDepartment.name})
                          <Tag minimal round>
                            Team
                          </Tag>
                        </>
                      )}
                    </div>
                    {!readOnly && (
                      <IconButton
                        small
                        minimal
                        tooltip="Remove"
                        iconName="times"
                        className="icon-button"
                        onClick={() => {
                          if (role !== Role.BOOKINGS_MANAGER) {
                            const reqId = Date.now()
                            deleteDepartment(department, reqId)
                            api.employee
                              .deleteDepartmentFromManagedDepartments({
                                id: employeeId,
                                departmentId: department.id,
                              })
                              .then((updatedManagedDepartments) => {
                                reset(updatedManagedDepartments, reqId)
                                Toaster.success({ message: 'Department deleted' })
                              })
                              .catch((error: ApolloError) => Toaster.fail({ error }))
                          } else {
                            deleteBookingManagerDepartment(department, empIds)
                          }
                        }}
                      />
                    )}
                  </Flex>
                </Item>
              )
            )}
            {!readOnly && (
              <Item>
                <DepartmentSelect
                  buttonProps={{ minimal: true, blue: true, iconName: 'plus' }}
                  value={null}
                  placeholder="Add department"
                  onChange={(newDepartment: IDepartment | null) => {
                    if (newDepartment) {
                      if (role !== Role.BOOKINGS_MANAGER) {
                        const reqId = Date.now()
                        addDepartment(newDepartment, reqId)
                        api.employee
                          .addDepartmentToManagedDepartements({
                            id: employeeId,
                            departmentId: newDepartment.id,
                          })
                          .then((updatedManagedDepartements) => {
                            reset(updatedManagedDepartements, reqId)
                            Toaster.success({ message: 'Department added' })
                          })
                          .catch((error: ApolloError) => Toaster.fail({ error }))
                      } else {
                        const isDepartmentUnchecked =
                          newDepartment &&
                          managedDepartments?.find(
                            ({ department }) => department.id === newDepartment.id
                          )
                        if (isDepartmentUnchecked) {
                          deleteBookingManagerDepartment(newDepartment, empIds)
                        } else {
                          addBookingManagerDepartment(newDepartment, empIds)
                        }
                      }
                    }
                  }}
                  itemDisabled={(dept) =>
                    !!managedDepartments?.find(({ department }) => department.id === dept.id)
                  }
                  multi={role === Role.BOOKINGS_MANAGER}
                />
              </Item>
            )}
          </List>
        )}
        {showDepartmentsList(role, bookingManagerSetting) && (
          <p style={{ marginTop: 10 }}>
            Adding/removing a department will automatically add/remove its teams
          </p>
        )}
      </div>
    </>
  )
}

function useManagedDepartments(
  employeeId: string,
  role: string,
  updateDeptArray: (params: string[]) => void,
  bookingManagerSetting: any
): IState & {
  addDepartment: (department: IDepartment, reqId: number) => void
  deleteDepartment: (department: IDepartment, reqId: number) => void
  addBookingManagerDepartment: (department: IDepartment, empIds: string[]) => void
  deleteBookingManagerDepartment: (department: IDepartment, empIds: string[]) => void
  reset: (managedDepartments: IManagedDepartment[], reqId: number) => void
} {
  const [state, dispatch] = useReducer(reducer, {
    reqId: 0,
    loading: true,
    managedDepartments: [],
  })

  useEffect(() => {
    const reqId = Date.now()
    dispatch({
      type: Actions.LOAD,
      managedDepartments: [],
      reqId,
    })
  }, [role])

  useEffect(() => {
    const reqId = Date.now()
    if (role !== Role.BOOKINGS_MANAGER) {
      api.employee
        .managedDepartments(employeeId)
        .then((managedDepartments) => {
          dispatch({
            type: Actions.LOAD,
            managedDepartments,
            reqId,
          })
        })
        .catch((error: ApolloError) => Toaster.fail({ error }))
    } else {
      const updatedArray = bookingManagerSetting?.managed_departments?.map((dept: any) => {
        return { department: dept }
      })
      const selectedDeptArray = updatedArray?.map(({ department }: any) => department.id)
      updateDeptArray(selectedDeptArray)
      dispatch({
        type: Actions.LOAD,
        managedDepartments: updatedArray || [],
        reqId,
      })
    }
  }, [employeeId, bookingManagerSetting])

  const updateBookingManagerDepartmentList = (
    managedDepartments: IManagedDepartment[],
    empIds: string[],
    message: string
  ) => {
    const selectedDeptArray = managedDepartments?.map(({ department }) => department.id)
    api.employee
      .updateDeptEmpForBookingManager({
        id: employeeId,
        bookingsManagerSetting: {
          managed_departments_ids: selectedDeptArray,
          managed_employees_ids: empIds,
          manage_all: false,
        },
        role: role,
      })
      .then(() => {
        dispatch({
          type: Actions.UPDATE,
          managedDepartments,
          reqId: Date.now(),
        })
        updateDeptArray(selectedDeptArray)
        Toaster.success({ message: message })
      })
  }
  const fetchTeamsList = (newDepartment: IDepartment) => {
    const PAGE_SIZE = 100
    return api.department.team.list({
      id: newDepartment.id,
      first: PAGE_SIZE,
      orderBy: 'name_ASC',
      after: '',
    })
  }

  return {
    ...state,
    addDepartment: (department: IDepartment, reqId: number) => {
      const exists = state.managedDepartments.find(({ department: { id } }) => department.id === id)

      if (exists) return

      dispatch({
        type: Actions.UPDATE,
        managedDepartments: [...state.managedDepartments, { department }],
        reqId,
      })
    },

    deleteDepartment: (department: IDepartment, reqId: number) => {
      dispatch({
        type: Actions.UPDATE,
        managedDepartments: state.managedDepartments.filter(
          ({ department: { id } }) => department.id !== id
        ),
        reqId,
      })
    },
    addBookingManagerDepartment(department: IDepartment, empIds: string[]) {
      new Promise((resolve) => {
        if (department?.totals?.teams > 0) {
          fetchTeamsList(department).then(({ teams }) => {
            resolve([...teams, department])
          })
        } else {
          resolve([department])
        }
      }).then((departments) => {
        const managedDepartments: IManagedDepartment[] = (departments as IDepartment[]).map(
          (department) => ({ department })
        )
        const filteredDepts: IManagedDepartment[] = uniqBy(
          [...managedDepartments, ...state.managedDepartments],
          'department.id' as ValueIteratee<IManagedDepartment>
        ) as unknown as IManagedDepartment[]
        updateBookingManagerDepartmentList(filteredDepts, empIds, 'Department added')
      })
    },
    deleteBookingManagerDepartment(department: IDepartment, empIds: string[]) {
      new Promise((resolve) => {
        if (department?.totals?.teams > 0) {
          fetchTeamsList(department).then(({ teams }) => {
            resolve([...teams, department])
          })
        } else if (department.parentDepartment) {
          resolve([department, department.parentDepartment])
        } else {
          resolve([department])
        }
      }).then((departments) => {
        const deletedDeptIds = (departments as IDepartment[]).map(({ id }) => id)
        const managedDept = [...state.managedDepartments]
        const managedDepartments = managedDept.filter(
          ({ department: { id } }) => !deletedDeptIds.includes(id)
        )
        updateBookingManagerDepartmentList(managedDepartments, empIds, 'Department deleted')
      })
    },
    reset: (managedDepartments, reqId) => {
      dispatch({
        type: Actions.LOAD,
        managedDepartments,
        reqId,
      })
    },
  }
}

function reducer(state: IState, action: Action) {
  if (state.reqId > action.reqId) {
    return state
  }

  switch (action.type) {
    case Actions.LOAD:
      return {
        loading: false,
        reqId: action.reqId,
        managedDepartments: action.managedDepartments,
      }

    case Actions.UPDATE:
      return {
        ...state,
        reqId: action.reqId,
        managedDepartments: action.managedDepartments,
      }

    default:
      return state
  }
}
